import { BRAND_ASSETS } from "./brandAssets";

export const SITE_ORIGIN = "https://stats.fan";

export const SPORT_SEO = {
  mlb: { id: "mlb", label: "MLB", name: "baseball" },
  nba: { id: "nba", label: "NBA", name: "basketball" },
  nfl: { id: "nfl", label: "NFL", name: "football" },
};

function sportSeo(sport) {
  return SPORT_SEO[sport] || SPORT_SEO.mlb;
}

const WORKSPACE_PAGES = {
  home: {
    title: (sport) => `${sport.label} stats and player comparisons | Stats.Fan`,
    description: (sport) =>
      `Explore ${sport.label} statistics, career leaders, teams, and head-to-head player comparisons.`,
  },
  players: {
    title: (sport) => `${sport.label} player search | Stats.Fan`,
    description: (sport) =>
      `Search ${sport.label} players, browse franchises, and open career stats.`,
  },
  player: {
    title: (sport) => `${sport.label} player | Stats.Fan`,
    description: (sport) =>
      `Career statistics and bio for an ${sport.label} player on Stats.Fan.`,
  },
  compare: {
    title: (sport) => `Compare ${sport.label} players | Stats.Fan`,
    description: (sport) =>
      `Compare ${sport.label} legends side by side with career totals and season charts.`,
  },
  teams: {
    title: (sport) => `${sport.label} teams | Stats.Fan`,
    description: (sport) =>
      `Browse ${sport.label} franchises and the players who suited up for each club.`,
  },
  team: {
    title: (sport) => `${sport.label} team | Stats.Fan`,
    description: (sport) =>
      `Roster and franchise history for an ${sport.label} team on Stats.Fan.`,
  },
  allTimeGreats: {
    title: (sport) => `${sport.label} all-time greats | Stats.Fan`,
    description: (sport) =>
      `Career leaderboards for the greatest ${sport.name} players in ${sport.label} history.`,
  },
  queryBuilder: {
    title: (sport) => `${sport.label} stat explorer | Stats.Fan`,
    description: (sport) =>
      `Build custom ${sport.label} stat queries and find players who match your filters.`,
  },
  trivia: {
    title: (sport) => `${sport.label} trivia | Stats.Fan`,
    description: (sport) =>
      `Play ${sport.label} trivia and put your ${sport.name} knowledge to the test.`,
  },
  lineup: {
    title: (sport) => `My ${sport.label} lineup | Stats.Fan`,
    description: (sport) =>
      `Build and share your all-time ${sport.label} lineup on Stats.Fan.`,
  },
  game: {
    title: (sport) => `${sport.label} game | Stats.Fan`,
    description: (sport) => `Game details and box-score stats on Stats.Fan.`,
  },
};

const ACCOUNT_PAGES = {
  dashboard: {
    title: "Dashboard | Stats.Fan",
    description:
      "Your Stats.Fan dashboard — saved views, trivia, and player collections.",
  },
  profile: {
    title: "Profile | Stats.Fan",
    description: "Manage your Stats.Fan profile and account settings.",
  },
  admin: {
    title: "Admin | Stats.Fan",
    description: "Stats.Fan admin tools.",
    robots: "noindex, nofollow",
  },
};

export const PUBLIC_SEO = {
  home: {
    title: "Stats.Fan — Settle the Debate | MLB, NBA & NFL Stats",
    description:
      "Compare legends and explore player stats across MLB, NBA, and NFL. Career numbers, head-to-head comparisons, and all-time greats — backed by real data.",
    keywords:
      "MLB statistics, NBA statistics, NFL statistics, baseball stats, basketball stats, football stats, player comparison, sports analytics, Stats.Fan",
    canonical: `${SITE_ORIGIN}/`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Stats.Fan",
      description:
        "Sports statistics platform for comparing, debating, and learning about the greatest MLB, NBA, and NFL players",
      url: `${SITE_ORIGIN}/`,
      image: BRAND_ASSETS.ogImageAbsolute,
      author: { "@type": "Person", name: "James Carvallyo" },
      applicationCategory: "SportsApplication",
      operatingSystem: "Web Browser",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },
  about: {
    title: "About Stats.Fan — MLB, NBA & NFL Statistics | James Carvallyo",
    description:
      "Learn about Stats.Fan, the MLB, NBA, and NFL statistics platform built by James Carvallyo. Sports data made fun, engaging, and accessible for fans.",
    keywords:
      "Stats.Fan about, James Carvallyo, MLB NBA NFL statistics, sports analytics, baseball basketball football stats",
    canonical: `${SITE_ORIGIN}/about`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "About Stats.Fan",
      description:
        "The story behind Stats.Fan, an MLB, NBA, and NFL statistics platform",
      url: `${SITE_ORIGIN}/about`,
      author: { "@type": "Person", name: "James Carvallyo" },
      publisher: {
        "@type": "Organization",
        name: "Stats.Fan",
        logo: BRAND_ASSETS.ogImageAbsolute,
      },
    },
  },
  login: {
    title: "Sign in — Stats.Fan",
    description:
      "Sign in to Stats.Fan to compare MLB, NBA, and NFL players and settle debates with real data.",
    robots: "noindex, nofollow",
  },
  signup: {
    title: "Sign up — Stats.Fan",
    description:
      "Create a free Stats.Fan account to compare MLB, NBA, and NFL players, build collections, and explore career statistics.",
    robots: "noindex, nofollow",
  },
};

export function getPublicSeo(page = "home") {
  return PUBLIC_SEO[page] || PUBLIC_SEO.home;
}

export function getWorkspaceSeo({
  sport = "mlb",
  destination = "home",
  pathname,
} = {}) {
  const account = ACCOUNT_PAGES[destination];
  const sportMeta = sportSeo(sport);
  const page = account || WORKSPACE_PAGES[destination] || WORKSPACE_PAGES.home;
  const title =
    typeof page.title === "function" ? page.title(sportMeta) : page.title;
  const description =
    typeof page.description === "function"
      ? page.description(sportMeta)
      : page.description;

  return {
    title,
    description,
    keywords: account
      ? "Stats.Fan"
      : `${sportMeta.label} statistics, ${sportMeta.name} stats, player comparison, Stats.Fan`,
    canonical: pathname ? `${SITE_ORIGIN}${pathname}` : undefined,
    robots: page.robots || "index, follow",
  };
}
